/* ============================================================================
   ITG — Purchase Lock: site-wide guard
   ============================================================================
   Loaded on EVERY page (from layout/theme.liquid), unlike itg-purchase-lock.js
   which only loads where a locked product is rendered.

   WHY THIS FILE EXISTS
   --------------------
   The Liquid cart guard is evaluated when the page is RENDERED. If a locked
   product enters the cart afterwards — a wishlist app adding by AJAX, a
   /cart/add link, a stale drawer — the checkout button was already emitted as
   enabled and never re-evaluates. The customer could then reach checkout from
   the cart page or drawer.

   The wishlist app is also injected by a third party on any page, where nothing
   marks the product as locked, so the page-scoped guard cannot see it.

   THREE LAYERS, most robust first
   -------------------------------
   1. LIVE CART CHECK — reads the real cart via /cart.js, so it is immune to
      stale HTML. If a locked line is present it disables every checkout
      control it can find. This is the guarantee.
   2. REQUEST GUARD — patches fetch AND XMLHttpRequest so an add is rejected
      before it reaches Shopify. Does not depend on any app's markup.
   3. WISHLIST UI — replaces the app's Add to Cart with a "View details" link.
      Cosmetic; depends on the app's DOM, so treated as polish, not enforcement.

   FAIL-OPEN THROUGHOUT
   --------------------
   Every unknown becomes "not locked". A failed lookup, a network error or an
   unrecognised response leaves the product purchasable rather than breaking a
   working store. The wishlist, and every untagged product, must behave exactly
   as before.

   LIMITATION
   ----------
   Shopify's hosted /checkout page is not part of the theme, so no theme code
   runs there. Navigating straight to /checkout cannot be blocked from here.
   Only a Shopify Cart/Checkout Validation Function (a custom app) can do that.
   ========================================================================= */

(function () {
  "use strict";

  var config = window.ITGPurchaseLockConfig || {};
  var lockTags = (config.tags || [])
    .map(function (t) {
      return String(t).trim().toLowerCase();
    })
    .filter(Boolean);

  // No tags configured, or the feature is off -> do absolutely nothing.
  // Never patch fetch/XHR or touch the DOM in that case.
  if (!lockTags.length) return;

  var routes = window.routes || {};
  var cartUrl = routes.cart_url || "/cart";
  var cartChangeUrl = routes.cart_change_url || "/cart/change";

  /* ── Lock lookup, cached ───────────────────────────────────────────────── */

  // handle -> true | false, and in-flight promises so we never fetch twice.
  var lockedByHandle = {};
  var pendingByHandle = {};
  // variant id (string) -> true | false, filled in as we learn products.
  var lockedByVariant = {};

  function tagsAreLocked(tags) {
    if (!tags || !tags.length) return false;
    for (var i = 0; i < tags.length; i++) {
      var tag = String(tags[i]).trim().toLowerCase();
      if (lockTags.indexOf(tag) !== -1) return true;
    }
    return false;
  }

  /*
   * Resolves a product handle to locked/not via Shopify's product JSON, which
   * includes the tag list. Cached per handle. Resolves false on any failure so
   * a lookup problem can never make a product unbuyable.
   */
  function isProductLocked(handle) {
    if (!handle) return Promise.resolve(false);
    if (lockedByHandle.hasOwnProperty(handle)) {
      return Promise.resolve(lockedByHandle[handle]);
    }
    if (pendingByHandle[handle]) return pendingByHandle[handle];

    var request = nativeFetch(window.shopUrl ? "/products/" + handle + ".js" : "/products/" + handle + ".js", {
      headers: { Accept: "application/json" },
    })
      .then(function (res) {
        if (!res.ok) throw new Error("lookup failed");
        return res.json();
      })
      .then(function (product) {
        var locked = tagsAreLocked(product.tags);
        lockedByHandle[handle] = locked;

        // Remember every variant so the request guard can act without a lookup.
        (product.variants || []).forEach(function (variant) {
          lockedByVariant[String(variant.id)] = locked;
        });

        return locked;
      })
      .catch(function () {
        lockedByHandle[handle] = false; // fail open
        return false;
      })
      .then(function (locked) {
        delete pendingByHandle[handle];
        return locked;
      });

    pendingByHandle[handle] = request;
    return request;
  }

  function handleFromUrl(url) {
    if (!url) return null;
    var match = String(url).match(/\/products\/([a-z0-9\-_%]+)/i);
    if (!match) return null;
    return match[1].split("?")[0].split("#")[0];
  }

  /* ── Layer 1: live cart check — the guarantee ──────────────────────────── */

  var CHECKOUT_SELECTORS = [
    "#checkout",
    "#CartDrawer-Checkout",
    'button[name="checkout"]',
    'input[name="checkout"]',
    ".cart__checkout-button",
    ".sl-cart-checkout",
  ].join(",");

  function disableCheckoutControls() {
    // Form-submit checkout buttons: disable in place.
    document.querySelectorAll(CHECKOUT_SELECTORS).forEach(function (el) {
      el.disabled = true;
      el.setAttribute("aria-disabled", "true");
      el.setAttribute("data-itg-plock-blocked", "true");
    });

    // Anchor-based checkout (the Skylabs drawer uses a plain link, where the
    // disabled attribute does nothing). Neutralise the href instead.
    document.querySelectorAll('a[href*="/checkout"]').forEach(function (el) {
      if (el.getAttribute("data-itg-plock-blocked")) return;
      el.setAttribute("data-itg-plock-href", el.getAttribute("href") || "");
      el.removeAttribute("href");
      el.setAttribute("aria-disabled", "true");
      el.setAttribute("data-itg-plock-blocked", "true");
      el.style.pointerEvents = "none";
      el.style.opacity = "0.5";
    });

    // Dynamic checkout (Shop Pay etc.) bypasses the form entirely.
    document
      .querySelectorAll(".additional-checkout-buttons, .cart__dynamic-checkout-buttons")
      .forEach(function (el) {
        el.style.display = "none";
      });
  }

  function showCartNotice(titles) {
    if (document.querySelector("[data-itg-plock-live-notice]")) return;

    var host =
      document.querySelector(".cart__items") ||
      document.querySelector("#CartDrawer-CartItems") ||
      document.querySelector("#SkyLabsCartBody") ||
      document.querySelector(".sl-cart-drawer__body");
    if (!host) return;

    var notice = document.createElement("div");
    notice.setAttribute("data-itg-plock-live-notice", "");
    notice.setAttribute("role", "alert");
    // Inline styles: this file is loaded site-wide, so it must not pull in the
    // feature stylesheet on every page just to render an occasional notice.
    notice.style.cssText =
      "display:flex;gap:10px;padding:14px 18px;margin-bottom:16px;" +
      "background:rgba(192,57,43,0.1);border:1px solid rgba(192,57,43,0.4);" +
      "border-radius:12px;font-size:1.4rem;line-height:1.5;";

    // Same sentence shape as the Liquid notice in
    // snippets/itg-purchase-lock-cart-notice.liquid, so the two are
    // indistinguishable. Product names always come from the live cart.
    var message =
      config.cartMessage ||
      "This item cannot be purchased online. Please use the request form on the product page.";

    if (titles.length) {
      var base = message.replace(/\s*\.\s*$/, "");
      notice.textContent =
        base + " for “" + titles.join("”, “") + ".”";
    } else {
      notice.textContent = message;
    }

    host.insertBefore(notice, host.firstChild);
  }

  function removeLines(keys) {
    return keys.reduce(function (chain, key) {
      return chain.then(function () {
        return nativeFetch(cartChangeUrl + ".js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: key, quantity: 0 }),
        });
      });
    }, Promise.resolve());
  }

  var verifyScheduled = false;

  /*
   * Reads the REAL cart and blocks checkout if any line is locked. Immune to
   * stale server-rendered HTML, which is the bug this fixes.
   */
  function verifyCart() {
    if (verifyScheduled) return;
    verifyScheduled = true;

    nativeFetch(cartUrl + ".js", { headers: { Accept: "application/json" } })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (cart) {
        verifyScheduled = false;
        if (!cart || !cart.items || !cart.items.length) return;

        return Promise.all(
          cart.items.map(function (item) {
            return isProductLocked(item.handle).then(function (locked) {
              return locked ? item : null;
            });
          }),
        ).then(function (results) {
          var locked = results.filter(Boolean);
          if (!locked.length) return;

          disableCheckoutControls();
          showCartNotice(
            locked.map(function (item) {
              return item.product_title || item.title;
            }),
          );

          if (config.autoremove) {
            var keys = locked.map(function (item) {
              return item.key;
            });
            // Only reload on a cart page/drawer, so a shopper browsing
            // elsewhere is not yanked out of what they were doing.
            removeLines(keys).then(function () {
              if (window.location.pathname.indexOf("/cart") !== -1) {
                window.location.reload();
              }
            });
          }
        });
      })
      .catch(function () {
        verifyScheduled = false; // fail open
      });
  }

  /* ── Layer 2: request guard — fetch and XMLHttpRequest ─────────────────── */

  var nativeFetch =
    typeof window.fetch === "function" ? window.fetch.bind(window) : null;

  function variantIdsFromBody(body) {
    var ids = [];
    function push(value) {
      if (value === undefined || value === null) return;
      ids.push(String(value).trim());
    }
    try {
      if (typeof body === "string") {
        if (body.trim().charAt(0) === "{") {
          var parsed = JSON.parse(body);
          push(parsed.id);
          if (Array.isArray(parsed.items)) {
            parsed.items.forEach(function (item) {
              push(item.id);
            });
          }
        } else {
          new URLSearchParams(body).getAll("id").forEach(push);
        }
      } else if (body instanceof FormData || body instanceof URLSearchParams) {
        body.getAll("id").forEach(push);
      }
    } catch (err) {
      return []; // unparseable -> do not block
    }
    return ids;
  }

  // Only blocks variants we KNOW are locked. An unknown variant is allowed
  // through and caught a moment later by the live cart check above.
  function bodyHasLockedVariant(body) {
    return variantIdsFromBody(body).some(function (id) {
      return lockedByVariant[id] === true;
    });
  }

  function installRequestGuards() {
    if (nativeFetch) {
      window.fetch = function (input, init) {
        try {
          var url =
            typeof input === "string" ? input : input && input.url ? input.url : "";

          if (url.indexOf("/cart/add") !== -1) {
            var body = init && init.body ? init.body : null;
            if (bodyHasLockedVariant(body)) {
              verifyCart();
              return Promise.reject(
                new Error("ITG Purchase Lock: this product cannot be purchased online."),
              );
            }
            // Unknown variant: let it through, then verify the cart.
            var result = nativeFetch.apply(this, arguments);
            return result.then(function (res) {
              verifyCart();
              return res;
            });
          }
        } catch (err) {
          // Never break a legitimate request.
        }
        return nativeFetch.apply(this, arguments);
      };
    }

    // Apps commonly use XHR (jQuery.ajax does), which the previous guard missed.
    if (typeof window.XMLHttpRequest === "function") {
      var NativeXHR = window.XMLHttpRequest;
      var open = NativeXHR.prototype.open;
      var send = NativeXHR.prototype.send;

      NativeXHR.prototype.open = function (method, url) {
        try {
          this.__itgPlockCartAdd =
            typeof url === "string" && url.indexOf("/cart/add") !== -1;
        } catch (err) {
          this.__itgPlockCartAdd = false;
        }
        return open.apply(this, arguments);
      };

      NativeXHR.prototype.send = function (body) {
        try {
          if (this.__itgPlockCartAdd) {
            if (bodyHasLockedVariant(body)) {
              verifyCart();
              this.abort();
              return;
            }
            this.addEventListener("load", function () {
              verifyCart();
            });
          }
        } catch (err) {
          // Fall through and send normally.
        }
        return send.apply(this, arguments);
      };
    }
  }

  /* ── Layer 3: wishlist panel UI ────────────────────────────────────────── */

  /*
   * Replaces the wishlist app's Add to Cart with a "View details" link for
   * locked products only.
   *
   * The app's markup is injected by a third party and can change without
   * notice, so this is deliberately generic and entirely fail-open: if the
   * structure is not recognised, nothing is touched and the wishlist keeps
   * working exactly as before. Enforcement does not rely on this layer.
   */
  function guardWishlistPanel(root) {
    var containers = (root || document).querySelectorAll(
      '[class*="swym"] a[href*="/products/"]',
    );

    containers.forEach(function (link) {
      var item = link.closest(
        '[class*="swym-wishlist-item"], [class*="swym-item"], li, .swym-ui-product-card',
      );
      if (!item || item.getAttribute("data-itg-plock-checked")) return;
      item.setAttribute("data-itg-plock-checked", "true");

      var handle = handleFromUrl(link.getAttribute("href"));
      if (!handle) return;

      isProductLocked(handle).then(function (locked) {
        if (!locked) return;

        var buttons = item.querySelectorAll(
          'button, a[class*="cart"], [class*="add-to-cart"], [class*="atc"]',
        );

        buttons.forEach(function (btn) {
          var text = (btn.textContent || "").toLowerCase();
          var cls = (btn.className || "").toString().toLowerCase();
          var looksLikeAdd =
            text.indexOf("add to cart") !== -1 ||
            text.indexOf("add to bag") !== -1 ||
            cls.indexOf("add-to-cart") !== -1 ||
            cls.indexOf("atc") !== -1;
          if (!looksLikeAdd) return;

          var replacement = document.createElement("a");
          replacement.href = link.getAttribute("href");
          replacement.className = btn.className;
          replacement.textContent = config.cardLabel || "View details";
          replacement.setAttribute("data-itg-plock-card", "true");
          btn.parentNode.replaceChild(replacement, btn);
        });
      });
    });
  }

  function initWishlistObserver() {
    guardWishlistPanel(document);

    if (typeof window.MutationObserver !== "function") return;

    var pending = null;
    var observer = new MutationObserver(function () {
      // The app re-renders often; batch to one pass per frame.
      if (pending) return;
      pending = window.setTimeout(function () {
        pending = null;
        guardWishlistPanel(document);
      }, 150);
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  /* ── Boot ─────────────────────────────────────────────────────────────── */

  function init() {
    installRequestGuards();
    verifyCart();
    initWishlistObserver();

    // Dawn publishes cart updates through section re-renders; re-verify after
    // any of them so a stale checkout button cannot survive.
    document.addEventListener("shopify:section:load", verifyCart);
    window.addEventListener("pageshow", verifyCart);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
